import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Minus, Plus, ShoppingBag, MessageCircle } from "lucide-react";
import PageShell from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/shop/CartContext";
import { inr, savingsBadge, offLabel } from "@/shop/products";
import { useProducts } from "@/shop/ProductsContext";
import { track } from "@/lib/pixel";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "919003140803";
const FEATURES = ["10g Plant Protein", "2g Horse gram Microgreens", "No Added Sugar", "Trans-Fat Free"];

const ProductDetail = () => {
  const { slug } = useParams();
  const { getProduct } = useProducts();
  const product = getProduct(slug);
  const { add } = useCart();
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) {
      track("ViewContent", {
        content_ids: [product.sku],
        content_type: "product",
        content_name: product.title,
        value: product.price,
        currency: "INR",
      });
    }
  }, [product?.slug]);

  if (!product) return <Navigate to="/shop" replace />;

  const off = offLabel(product);
  const save = savingsBadge(product);
  const inStock = product.in_stock !== false;
  const buyNow = () => {
    const msg = `Hi Zentra! I'd like to order: ${qty} × ${product.title} (${product.short}) — ${inr(product.price * qty)}. Please confirm availability and delivery details.`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <PageShell>
      <Helmet>
        <title>{`${product.title} | Zentra Shop`}</title>
        <meta name="description" content={`${product.title} — ${product.description}`} />
      </Helmet>
      <section className="px-5 py-14 lg:px-12" data-testid="product-detail-page">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={product.image} alt={product.title} className="w-full bg-[#0c0e0b] object-contain p-10" data-testid="product-detail-image" />
              {save && (
                <span className="absolute left-4 top-4 rounded-full bg-[#1A4D2E] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#b7e389]">
                  {save}
                </span>
              )}
              {!inStock && (
                <span className="absolute right-4 top-4 rounded-full bg-[#3a1215] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#f3a6a6]" data-testid="detail-out-of-stock">
                  Out of Stock
                </span>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/35">SKU: {product.sku}</p>
            <h1 className="mt-2 font-display text-3xl uppercase leading-[0.95] text-white sm:text-4xl" data-testid="product-detail-title">
              {product.title}
            </h1>
            <p className="mt-2 text-xs uppercase tracking-[0.15em] text-white/40">{product.short}</p>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display text-4xl text-white" data-testid="product-detail-price">{inr(product.price)}</span>
              {product.compareAt && (
                <>
                  <span className="text-lg text-white/35 line-through">{inr(product.compareAt)}</span>
                  <span className="rounded-full bg-[#7ED321]/10 px-3 py-1 text-[11px] font-bold text-[#7ED321]">
                    {off} — Save {inr(product.compareAt - product.price)}
                  </span>
                </>
              )}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-[#A6A66A]">{product.description}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              {FEATURES.map((f) => (
                <span key={f} className="rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-white/70">
                  {f}
                </span>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-white/15">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  data-testid="qty-minus"
                  aria-label="Decrease quantity"
                  className="flex h-11 w-11 items-center justify-center text-white/70 hover:text-[#7ED321]"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center text-sm font-semibold text-white" data-testid="qty-value">{qty}</span>
                <button
                  onClick={() => setQty(qty + 1)}
                  data-testid="qty-plus"
                  aria-label="Increase quantity"
                  className="flex h-11 w-11 items-center justify-center text-white/70 hover:text-[#7ED321]"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => add(product.slug, qty)}
                disabled={!inStock}
                data-testid="detail-add-to-cart"
                className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#7ED321] py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/40 disabled:hover:bg-white/10"
              >
                <ShoppingBag className="h-4 w-4" />
                {inStock ? "Add to Cart" : "Out of Stock"}
              </button>
            </div>
            <button
              onClick={buyNow}
              data-testid="detail-buy-whatsapp"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#2be374]"
            >
              <MessageCircle className="h-4 w-4" />
              Order on WhatsApp
            </button>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
};

export default ProductDetail;
